import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Send, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SECONDS = 300; // 5 minutes
const WAVEFORM_BARS = 40;

function fmt(sec) {
  const s = Math.floor(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * VoiceRecorder — wraps a mic button. When recording, shows a full-width overlay
 * with a live timer and cancel/stop controls. Calls `onSend({ blob, duration, peaks })`.
 */
export default function VoiceRecorder({ onSend, disabled }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [sending, setSending] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const peaksRef = useRef([]); // running max values for waveform sampling
  const peakBucketRef = useRef(0);
  const peakIdxRef = useRef(0);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const cancelRef = useRef(false);

  const stopEverything = () => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
  };

  useEffect(() => () => { stopEverything(); }, []);

  const start = async () => {
    if (disabled || recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported on this browser');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error('Microphone permission denied');
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];
    peaksRef.current = new Array(WAVEFORM_BARS).fill(0);
    peakIdxRef.current = 0;
    peakBucketRef.current = 0;
    cancelRef.current = false;

    // Pick a supported mime type (Safari prefers mp4)
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mpeg',
    ];
    let mime = '';
    for (const m of candidates) {
      if (MediaRecorder.isTypeSupported(m)) { mime = m; break; }
    }
    const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    mr.onstop = async () => {
      const type = mr.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      const duration = Math.max(0.1, (performance.now() - startedAtRef.current) / 1000);
      stopEverything();
      setRecording(false);
      setSeconds(0);
      if (cancelRef.current) return;

      // Normalize peaks so min ≈ 0.05, max ≈ 1.0 — looks better visually
      const rawPeaks = peaksRef.current;
      const max = Math.max(...rawPeaks, 0.001);
      const peaks = rawPeaks.map(p => Math.max(0.05, Math.min(1, p / max)));

      setSending(true);
      try {
        await onSend({ blob, duration, peaks, mime: type });
      } catch (e) {
        toast.error(e.message || 'Failed to send voice message');
      } finally {
        setSending(false);
      }
    };

    // Set up waveform sampling via Web Audio API
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyserRef.current = analyser;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const BUCKET_SAMPLES = Math.ceil((MAX_SECONDS * 60) / WAVEFORM_BARS); // rough spread across max duration
      const sample = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        // peak = max deviation from silence (128)
        let peak = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = Math.abs(buf[i] - 128) / 128;
          if (v > peak) peak = v;
        }
        if (peak > peakBucketRef.current) peakBucketRef.current = peak;
        // advance bucket every N samples (~sampleRate/fftSize*N ms)
        // We cycle through bars so long clips still produce meaningful peaks
        const bucketSize = BUCKET_SAMPLES;
        const now = performance.now();
        const elapsedMs = now - startedAtRef.current;
        const targetIdx = Math.min(WAVEFORM_BARS - 1, Math.floor((elapsedMs / (MAX_SECONDS * 1000)) * WAVEFORM_BARS));
        if (targetIdx !== peakIdxRef.current) {
          peaksRef.current[peakIdxRef.current] = peakBucketRef.current;
          peakBucketRef.current = 0;
          peakIdxRef.current = targetIdx;
        } else {
          peaksRef.current[peakIdxRef.current] = Math.max(
            peaksRef.current[peakIdxRef.current], peakBucketRef.current
          );
        }
        void bucketSize;
        rafRef.current = requestAnimationFrame(sample);
      };
      rafRef.current = requestAnimationFrame(sample);
    } catch (e) {
      // Non-fatal — we just won't have a waveform
      console.warn('Waveform sampling unavailable', e);
    }

    startedAtRef.current = performance.now();
    setRecording(true);
    setSeconds(0);
    mr.start(250);
    timerRef.current = setInterval(() => {
      const elapsed = (performance.now() - startedAtRef.current) / 1000;
      setSeconds(elapsed);
      if (elapsed >= MAX_SECONDS) {
        stop();
      }
    }, 200);
  };

  const stop = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
  };

  const cancel = () => {
    cancelRef.current = true;
    stop();
  };

  if (sending) {
    return (
      <div
        data-testid="voice-sending"
        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--parity-surface-muted)] border border-[var(--parity-hairline)]"
      >
        <Loader2 size={14} className="animate-spin text-[var(--parity-ink)]" />
        <span className="text-[12px] text-[var(--parity-ink-soft)] font-medium">Sending voice message…</span>
      </div>
    );
  }

  if (recording) {
    const pct = Math.min(100, (seconds / MAX_SECONDS) * 100);
    return (
      <div
        data-testid="voice-recording-bar"
        className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--parity-surface-muted)] border border-[var(--parity-hairline)]"
      >
        <button
          type="button"
          onClick={cancel}
          data-testid="voice-cancel"
          className="w-9 h-9 rounded-full bg-white border border-black/[0.08] flex items-center justify-center text-[#4B4B52] hover:bg-black/[0.02] active:scale-90 transition"
          aria-label="Cancel recording"
        >
          <X size={14} />
        </button>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--parity-ink)] animate-pulse flex-shrink-0" />
          <span className="text-[12px] font-semibold tabular-nums text-[var(--parity-ink)]">{fmt(seconds)}</span>
          <div className="flex-1 h-1 bg-black/[0.08] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--parity-ink)] transition-[width]" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] text-[var(--parity-ink-soft)] tabular-nums flex-shrink-0">5:00</span>
        </div>
        <button
          type="button"
          onClick={stop}
          data-testid="voice-send"
          className="w-9 h-9 rounded-full flex items-center justify-center text-white active:scale-90 transition bg-[var(--parity-ink)] hover:opacity-90"
          aria-label="Send voice message"
        >
          <Send size={14} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      data-testid="voice-record-btn"
      className="w-12 h-12 rounded-xl bg-[var(--parity-surface-muted)] border border-[var(--parity-hairline)] flex items-center justify-center text-[var(--parity-ink)] hover:opacity-80 active:scale-90 disabled:opacity-30 transition-opacity"
      aria-label="Record voice message"
    >
      <Mic size={16} />
    </button>
  );
}
